/**
 * The same rules the API enforces (PasswordRules on the server), checked as
 * the person types so the form never has to bounce back.
 */
import { i18n } from '@/i18n';

export const PASSWORD_MIN = 10;

const t = (key, params) => i18n.global.t(`study.password.${key}`, params);

/** [{ id, label, ok }] — each requirement for a new password, met or not. */
export function passwordChecks(password, confirmation) {
  return [
    { id: 'length', label: t('length', { n: PASSWORD_MIN }), ok: password.length >= PASSWORD_MIN },
    { id: 'letters', label: t('letters'), ok: /\p{L}/u.test(password) },
    { id: 'numbers', label: t('numbers'), ok: /\p{N}/u.test(password) },
    { id: 'match', label: t('match'), ok: password.length > 0 && password === confirmation },
  ];
}

/**
 * 0–4, how hard the password would be to guess: length counts most, then
 * the kinds of characters in it; a run of one character or a common word
 * pulls it back down. A guide for the meter, not a gate.
 */
export function passwordStrength(password) {
  if (!password) return 0;
  const kinds = [/\p{Ll}/u, /\p{Lu}/u, /\p{N}/u, /[^\p{L}\p{N}]/u].filter((re) => re.test(password)).length;
  let score = password.length >= 16 ? 3 : password.length >= 12 ? 2 : password.length >= PASSWORD_MIN ? 1 : 0;
  score += kinds >= 3 ? 1 : 0;
  if (/(.)\1{3,}/.test(password) || /^(password|sifra|šifra|deenly|qwerty|123456|admin)/i.test(password)) score -= 2;
  return Math.max(0, Math.min(4, score));
}

export const strengthLabel = (score) => t(`strength.${score}`);

/**
 * The API's validation answer as { field: 'first message' }, or {} when the
 * failure was something else (network, server).
 */
export function fieldErrors(error) {
  const errors = error?.response?.data?.errors;
  if (!errors || typeof errors !== 'object') return {};
  return Object.fromEntries(Object.entries(errors).map(([field, messages]) => [field, [].concat(messages)[0]]));
}

/** A sentence for a failed request that isn't about one field. */
export function requestError(error, fallback = t('failed')) {
  if (!error?.response) return t('offline');
  if (error.response.status === 429) return t('tooMany');
  return error.response.data?.message && error.response.data.message !== 'error' ? error.response.data.message : fallback;
}
